import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import API_BASE_URL from "../config/api";


function AddPaper() {

    const navigate = useNavigate();


    /*
    |--------------------------------------------------------------------------
    | Form Data
    |--------------------------------------------------------------------------
    */

    const [formData, setFormData] = useState({

        paper_type: "",

        paper: "",

        category: "",

        exam_date: ""

    });


    /*
    |--------------------------------------------------------------------------
    | Categories
    |--------------------------------------------------------------------------
    */

    const [categories, setCategories] = useState([]);


    /*
    |--------------------------------------------------------------------------
    | Loading
    |--------------------------------------------------------------------------
    */

    const [loading, setLoading] = useState(false);

    const [categoryLoading, setCategoryLoading] =
        useState(true);


    /*
    |--------------------------------------------------------------------------
    | Fetch Paper Categories
    |--------------------------------------------------------------------------
    */

    const fetchCategories = async () => {

        try {

            setCategoryLoading(true);


            const response = await fetch(
                `${API_BASE_URL}/admin/paper-category`,
                {
                    method: "GET",

                    headers: {
                        "Accept": "application/json"
                    },

                    credentials: "include"
                }
            );


            const data = await response.json();


            console.log(
                "Paper Category Response:",
                data
            );


            if (
                response.ok &&
                data.success
            ) {

                setCategories(
                    data.data || []
                );

            } else {

                Swal.fire({

                    icon: "error",

                    title: "Unable to Load Categories",

                    text:
                        data.message ||
                        "Unable to fetch paper categories."

                });

            }


        } catch (error) {

            console.error(
                "Fetch Categories Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    "Unable to connect with server."

            });

        } finally {

            setCategoryLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | Load Categories
    |--------------------------------------------------------------------------
    */

    useEffect(() => {

        fetchCategories();

    }, []);


    /*
    |--------------------------------------------------------------------------
    | Handle Change
    |--------------------------------------------------------------------------
    */

    const handleChange = (e) => {

        const {
            name,
            value
        } = e.target;


        setFormData({

            ...formData,

            [name]: value

        });

    };


    /*
    |--------------------------------------------------------------------------
    | Reset Form
    |--------------------------------------------------------------------------
    */

    const handleReset = () => {

        setFormData({

            paper_type: "",

            paper: "",

            category: "",

            exam_date: ""

        });

    };


    /*
    |--------------------------------------------------------------------------
    | Submit
    |--------------------------------------------------------------------------
    */

    const handleSubmit = async (e) => {

        e.preventDefault();


        /*
        |--------------------------------------------------------------------------
        | Prevent Multiple Submit
        |--------------------------------------------------------------------------
        */

        if (loading) {

            return;

        }


        /*
        |--------------------------------------------------------------------------
        | Validation
        |--------------------------------------------------------------------------
        */

        if (
            !formData.paper_type.trim()
        ) {

            Swal.fire({

                icon: "warning",

                title: "Paper Type Required",

                text:
                    "Please enter paper type."

            });

            return;

        }


        if (
            !formData.paper.trim()
        ) {

            Swal.fire({

                icon: "warning",

                title: "Paper Required",

                text:
                    "Please enter paper name."

            });

            return;

        }


        if (
            !formData.category.trim()
        ) {

            Swal.fire({

                icon: "warning",

                title: "Category Required",

                text:
                    "Please select paper category."

            });

            return;

        }


        if (
            !formData.exam_date
        ) {

            Swal.fire({

                icon: "warning",

                title: "Exam Date Required",

                text:
                    "Please select exam date."

            });

            return;

        }


        try {

            setLoading(true);


            /*
            |--------------------------------------------------------------------------
            | API Request
            |--------------------------------------------------------------------------
            */

            const response = await fetch(

                `${API_BASE_URL}/admin/papers`,

                {

                    method: "POST",

                    headers: {

                        "Accept":
                            "application/json",

                        "Content-Type":
                            "application/json"

                    },

                    credentials: "include",

                    body: JSON.stringify({

                        paper_type:
                            formData.paper_type.trim(),

                        paper:
                            formData.paper.trim(),

                        category:
                            formData.category.trim(),

                        exam_date:
                            formData.exam_date

                    })

                }

            );


            const data =
                await response.json();


            console.log(
                "Add Paper Response:",
                data
            );


            /*
            |--------------------------------------------------------------------------
            | Success
            |--------------------------------------------------------------------------
            */

            if (
                response.ok &&
                data.success
            ) {

                await Swal.fire({

                    icon: "success",

                    title: "Paper Added!",

                    text:
                        data.message ||
                        "Paper added successfully.",

                    confirmButtonText:
                        "OK",

                    confirmButtonColor:
                        "#198754"

                });


                /*
                |--------------------------------------------------------------------------
                | Go To Papers
                |--------------------------------------------------------------------------
                */

                navigate("/papers");


                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Validation Error
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 422 &&
                data.errors
            ) {

                const errors =
                    Object.values(
                        data.errors
                    ).flat();


                Swal.fire({

                    icon: "error",

                    title: "Validation Error",

                    html:
                        errors
                            .map(
                                (error) =>
                                    `<div>${error}</div>`
                            )
                            .join(""),

                    confirmButtonText:
                        "OK"

                });


                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Unauthorized
            |--------------------------------------------------------------------------
            */

            if (
                response.status === 401 ||
                response.status === 403
            ) {

                Swal.fire({

                    icon: "error",

                    title: "Unauthorized",

                    text:
                        data.message ||
                        "You are not authorized to add paper.",

                    confirmButtonText:
                        "OK"

                });


                return;

            }


            /*
            |--------------------------------------------------------------------------
            | Other Error
            |--------------------------------------------------------------------------
            */

            Swal.fire({

                icon: "error",

                title: "Unable to Add Paper",

                text:
                    data.message ||
                    "Something went wrong while adding paper.",

                confirmButtonText:
                    "OK"

            });


        } catch (error) {

            console.error(
                "Add Paper Error:",
                error
            );


            Swal.fire({

                icon: "error",

                title: "Server Error",

                text:
                    "Unable to connect with server. Please try again.",

                confirmButtonText:
                    "OK"

            });

        } finally {

            setLoading(false);

        }

    };


    /*
    |--------------------------------------------------------------------------
    | JSX
    |--------------------------------------------------------------------------
    */

    return (

        <div className="container-fluid">


            <div className="row justify-content-center">


                <div className="col-lg-8">


                    <div className="card shadow-sm">


                        {/* ====================================================
                            HEADER
                        ===================================================== */}

                        <div className="card-header bg-primary text-white">

                            <h4 className="mb-0">

                                Add Paper

                            </h4>

                        </div>


                        {/* ====================================================
                            BODY
                        ===================================================== */}

                        <div className="card-body">


                            <form
                                onSubmit={
                                    handleSubmit
                                }
                            >


                                {/* ============================================
                                    Paper Type
                                ============================================= */}

                                <div className="mb-3">

                                    <label
                                        className="
                                            form-label
                                            fw-semibold
                                        "
                                    >

                                        Paper Type

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <select
                                        className="form-select"
                                        name="paper_type"
                                        value={formData.paper_type}
                                        onChange={handleChange}
                                        required
                                    >

                                        <option value="">
                                            Select Paper Type
                                        </option>

                                        <option value="Free">
                                            Free
                                        </option>

                                        <option value="Paid">
                                            Paid
                                        </option>

                                    </select>

                                </div>


                                {/* ============================================
                                    Paper
                                ============================================= */}

                                <div className="mb-3">

                                    <label
                                        className="
                                            form-label
                                            fw-semibold
                                        "
                                    >

                                        Paper

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <input
                                        type="text"
                                        className="form-control"
                                        name="paper"
                                        placeholder="Enter Paper Name"
                                        value={
                                            formData.paper
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        maxLength="20"
                                        required
                                    />

                                </div>


                                {/* ============================================
                                    Category
                                ============================================= */}

                                <div className="mb-3">

                                    <label
                                        className="
                                            form-label
                                            fw-semibold
                                        "
                                    >

                                        Category

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <select
                                        className="form-select"
                                        name="category"
                                        value={
                                            formData.category
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        required
                                        disabled={
                                            categoryLoading
                                        }
                                    >

                                        <option value="">

                                            {categoryLoading
                                                ? "Loading categories..."
                                                : "Select Paper Category"
                                            }

                                        </option>


                                        {categories.map(
                                            (item) => (

                                                <option
                                                    key={
                                                        item.id
                                                    }
                                                    value={
                                                        item.paper ||
                                                        item.paper_category
                                                    }
                                                >

                                                    {
                                                        item.paper ||
                                                        item.paper_category
                                                    }

                                                </option>

                                            )
                                        )}

                                    </select>

                                </div>


                                {/* ============================================
                                    Exam Date
                                ============================================= */}

                                <div className="mb-4">

                                    <label
                                        className="
                                            form-label
                                            fw-semibold
                                        "
                                    >

                                        Exam Date

                                        <span className="text-danger">
                                            *
                                        </span>

                                    </label>


                                    <input
                                        type="date"
                                        className="form-control"
                                        name="exam_date"
                                        value={
                                            formData.exam_date
                                        }
                                        onChange={
                                            handleChange
                                        }
                                        required
                                    />

                                </div>


                                {/* ============================================
                                    BUTTONS
                                ============================================= */}

                                <div className="d-flex gap-2">


                                    {/* Save */}

                                    <button
                                        type="submit"
                                        className="btn btn-primary"
                                        disabled={
                                            loading
                                        }
                                    >

                                        {loading ? (

                                            <>

                                                <span
                                                    className="
                                                        spinner-border
                                                        spinner-border-sm
                                                        me-2
                                                    "
                                                    role="status"
                                                    aria-hidden="true"
                                                />

                                                Saving...

                                            </>

                                        ) : (

                                            "Save Paper"

                                        )}

                                    </button>


                                    {/* Reset */}

                                    <button
                                        type="button"
                                        className="btn btn-secondary"
                                        onClick={
                                            handleReset
                                        }
                                        disabled={
                                            loading
                                        }
                                    >

                                        Reset

                                    </button>


                                    {/* Back */}

                                    <button
                                        type="button"
                                        className="
                                            btn
                                            btn-outline-dark
                                        "
                                        onClick={() =>
                                            navigate(
                                                "/papers"
                                            )
                                        }
                                        disabled={
                                            loading
                                        }
                                    >

                                        Back

                                    </button>


                                </div>


                            </form>


                        </div>


                    </div>


                </div>


            </div>


        </div>

    );

}


export default AddPaper;